import { useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import { formatElapsed, useElapsed } from '../lib/elapsed';
import { onTimerChange, timerChanged } from '../lib/timer';
import type { Project, RunningTimer, WorkItemCard } from '../lib/types';

interface Props {
  projects: Project[];
  onChanged: () => void | Promise<void>;
}

/** Start, stop or discard the running timer. One per person, enforced by the API. */
export function TimerBar({ projects, onChanged }: Props) {
  const [timer, setTimer] = useState<RunningTimer | null | undefined>(undefined);
  const [projectId, setProjectId] = useState('');
  const [tasks, setTasks] = useState<WorkItemCard[]>([]);
  const [workItemId, setWorkItemId] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ask = useAsk();

  async function load() {
    try {
      setTimer(await api.get<RunningTimer | null>('/time/timer'));
    } catch {
      setTimer(null);
    }
  }

  useEffect(() => {
    void load();
    /* The task drawer and the shell's chip can change this timer too. See `lib/timer`. */
    return onTimerChange(() => void load());
  }, []);

  /**
   * The same clock the task panel and the shell's chip use, counted from `startedAt` and
   * ticking every second. It was a half-minute re-render showing whole minutes, which on a
   * timer somebody has just started means watching "0m" and wondering whether it took.
   */
  const elapsed = useElapsed(timer?.startedAt);

  // Tasks for the chosen project, so time lands on the right thing.
  useEffect(() => {
    if (!projectId) {
      setTasks([]);
      return;
    }
    api
      .get<{ columns: Array<{ items: WorkItemCard[] }> }>(`/projects/${projectId}/board`)
      .then((board) => setTasks(board.columns.flatMap((c) => c.items)))
      .catch(() => setTasks([]));
  }, [projectId]);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      /* Everything else showing this timer, at once. */
      timerChanged();
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  if (timer === undefined) return null;


  return (
    <div className="card card-pad mb-2" style={{ borderLeft: '3px solid var(--accent)' }}>
      {error && <div className="callout">{error}</div>}

      {timer ? (
        <div className="row-between wrap">
          <div>
            <div className="row" style={{ gap: 9 }}>
              <span className="badge badge-accent">running</span>
              <strong className="running-timer-clock">{formatElapsed(elapsed)}</strong>
              <span className="muted">on {timer.projectName}</span>
              {timer.workItem && <span className="key">{timer.workItem.key}</span>}
            </div>
            {timer.description && (
              <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>
                {timer.description}
              </div>
            )}
          </div>
          <div className="row">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(async () => void (await api.post('/time/timer/stop')))}>
              Stop and book
            </button>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const sure = await ask.confirm({
                    title: 'Discard this timer?',
                    body: 'The time you have run is thrown away rather than booked against anything.',
                    confirmLabel: 'Discard it',
                    danger: true,
                  });
                  if (!sure) return;
                  await api.post('/time/timer/cancel');
                })
              }
            >
              Discard
            </button>
          </div>
        </div>
      ) : (
        <div className="row wrap" style={{ gap: 9, alignItems: 'flex-end' }}>
          <label className="field" style={{ marginBottom: 0, minWidth: 190 }}>
            <span className="field-label">Project</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Choose a project…</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} · {project.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field" style={{ marginBottom: 0, minWidth: 190 }}>
            <span className="field-label">Task (optional)</span>
            <select value={workItemId} onChange={(e) => setWorkItemId(e.target.value)} disabled={!projectId}>
              <option value="">No particular task</option>
              {tasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.key} · {task.title.slice(0, 40)}
                </option>
              ))}
            </select>
          </label>

          <label className="field grow" style={{ marginBottom: 0, minWidth: 170 }}>
            <span className="field-label">What are you doing?</span>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />
          </label>

          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !projectId}
            onClick={() =>
              act(async () => {
                await api.post('/time/timer/start', {
                  projectId,
                  workItemId: workItemId || undefined,
                  description: description || undefined,
                });
                setDescription('');
                setWorkItemId('');
              })
            }
          >
            Start timer
          </button>
        </div>
      )}
    </div>
  );
}
